import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, LogOut } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const navLinks = [
  { to: "/", label: "Home", end: true },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/grant-writer", label: "Grant Engine" },
  { to: "/case-studies", label: "Case Studies" },
  { to: "/pricing", label: "Pricing" },
  { to: "/resources", label: "Resources" },
  { to: "/compliance", label: "Compliance" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  const doLogout = async () => {
    await logout();
    nav("/");
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 glass" data-testid="site-navbar">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
          <img
            src="/phm-logo.png"
            alt="PHMEG"
            className="w-10 h-10 object-cover border border-[var(--border)] group-hover:border-[var(--red)] transition-colors"
          />
          <div className="leading-tight">
            <div className="font-display font-semibold text-sm tracking-tight">PHMEG</div>
            <div className="text-[10px] text-[var(--text-3)] uppercase tracking-[0.2em]">Health Equity • Evaluation</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {navLinks
            .filter((n) => n.to !== "/grant-writer" || (user && user.role === "admin"))
            .map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${isActive ? "text-white" : "text-[var(--text-2)] hover:text-white"}`
              }
              data-testid={`nav-${n.label.toLowerCase()}`}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user && user.role ? (
            <>
              <Link to="/book-consultation" className="btn-red !py-2.5 !px-5 text-base" data-testid="nav-book">Book consultation</Link>
              <Link
                to={user.role === "admin" ? "/admin" : "/portal"}
                className="btn-ghost !py-2 !px-4 text-sm"
                data-testid="nav-portal"
              >
                {user.role === "admin" ? "Admin" : "Portal"}
              </Link>
              <button onClick={doLogout} className="text-sm text-[var(--text-2)] hover:text-white flex items-center gap-1.5" data-testid="nav-logout">
                <LogOut className="w-4 h-4" /> Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/member" className="text-sm text-[var(--text-2)] hover:text-white" data-testid="nav-member-portal">Member Portal</Link>
              <Link to="/login" className="text-sm text-[var(--text-2)] hover:text-white" data-testid="nav-login">Sign in</Link>
              <Link to="/book-consultation" className="btn-red !py-2.5 !px-5 text-base" data-testid="nav-book">Book consultation</Link>
            </>
          )}
        </div>

        <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-[var(--bg)] border-t border-[var(--border)] px-6 py-4 flex flex-col gap-4" data-testid="mobile-menu">
          {navLinks
            .filter((n) => n.to !== "/grant-writer" || (user && user.role === "admin"))
            .map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)} className="text-sm text-white">{n.label}</NavLink>
          ))}
          {user && user.role ? (
            <>
              <Link to={user.role === "admin" ? "/admin" : "/portal"} onClick={() => setOpen(false)} className="btn-ghost !py-2 text-sm">{user.role === "admin" ? "Admin Dashboard" : "Client Portal"}</Link>
              <Link to="/book-consultation" onClick={() => setOpen(false)} className="btn-red !py-2.5 !px-5 text-base w-fit" data-testid="mobile-nav-book">Book consultation</Link>
              <button onClick={() => { setOpen(false); doLogout(); }} className="text-sm text-left text-[var(--text-2)]">Logout</button>
            </>
          ) : (
            <>
              <Link to="/member" onClick={() => setOpen(false)} className="text-sm text-white" data-testid="mobile-nav-member-portal">Member Portal</Link>
              <Link to="/login" onClick={() => setOpen(false)} className="text-sm text-white">Sign in</Link>
              <Link to="/book-consultation" onClick={() => setOpen(false)} className="btn-red !py-2.5 !px-5 text-base w-fit" data-testid="mobile-nav-book">Book consultation</Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}
